/**
 * /conversations/:id — Conversation detail page (Story 7.10).
 *
 * Layout:
 *   - Header: masked phone + name, opted-out badge, campaign link, status
 *     badge, opened/closed timestamps.
 *   - Scheduled callbacks panel (if any): list with scheduled time + status.
 *   - Timeline: WhatsApp-style via `MessageTimeline`.
 *   - "Encerrar conversa" button (operator+) — opens a confirmation dialog,
 *     calls POST /admin/conversations/:id/close, toasts on success and refetches.
 *
 * Error boundary: when `:id` isn't a valid conversation (API 404), we render
 * an inline fallback with a link back to the list.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Clock, Power } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import { ApiError, apiClient } from '@/lib/api';
import { formatDateTime as fmtDateTime } from '@/lib/format-date';
import { useAuth } from '@/hooks/useAuth';
import { StatusBadge, type ConversationStatus } from '@/components/conversations/StatusBadge';
import { MessageTimeline, type TimelineMessage } from '@/components/conversations/MessageTimeline';
import {
  FollowupExecutionCard,
  type FollowupExecutionListItem,
} from '@/components/conversations/FollowupExecutionCard';
import { HandoffBanner } from '@/components/conversations/HandoffBanner';
import { HandoffActions } from '@/components/conversations/HandoffActions';
import { ManualComposer } from '@/components/conversations/ManualComposer';
import { RetryFollowupModal } from '@/components/followups/RetryFollowupModal';

const ROUTABLE_STATUSES: ReadonlySet<ConversationStatus> = new Set([
  'active',
  'awaiting_response',
  'scheduled_callback',
]);

interface ConversationDetail {
  id: string;
  // Status pode ser ROUTABLE, terminal ou 'human_handoff' (Story 12.1)
  status: ConversationStatus;
  closedReason: string | null;
  createdAt: string;
  updatedAt: string;
  lastMessageAt: string | null;
  messageCount: number;
  /** JSONB com snapshot de handoff_by_user_id/name/at quando em handoff. */
  metadata: unknown;
  customer: {
    id: string;
    name: string | null;
    maskedPhone: string;
    optedOut: boolean;
    optedOutAt: string | null;
  };
  campaign: {
    id: string;
    name: string;
    active: boolean;
  };
  messages: TimelineMessage[];
  scheduledCallbacks: Array<{
    id: string;
    scheduledFor: string;
    status: 'pending' | 'executed' | 'cancelled';
  }>;
}

function humanizeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { code?: string; message?: string } };
    return body?.error?.message ?? `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

function formatTs(iso: string | null | undefined): string {
  return fmtDateTime(iso ?? null) || '—';
}

export function ConversationDetailPage() {
  const params = useParams<{ id: string }>();
  const id = params.id ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canClose = user?.role === 'operator' || user?.role === 'admin';
  const canRetryFollowup = user?.role === 'operator' || user?.role === 'admin';

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [closeReason, setCloseReason] = useState('manual_operator');
  const [retryOpen, setRetryOpen] = useState(false);

  const detailQuery = useQuery({
    queryKey: ['conversations', 'detail', id],
    queryFn: () => apiClient.get<ConversationDetail>(`/admin/conversations/${id}`),
    enabled: id.length > 0,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
    // Story 12.3 — polling adaptativo: 10s durante handoff (operador precisa
    // ver inbounds do lead rápido), 30s em ROUTABLE (status quo), nada em
    // status terminais.
    refetchInterval: (q) => {
      const data = q.state.data as ConversationDetail | undefined;
      if (!data) return false;
      if (data.status === 'human_handoff') return 10_000;
      if (ROUTABLE_STATUSES.has(data.status)) return 30_000;
      return false;
    },
  });

  const invalidateDetail = () => {
    void queryClient.invalidateQueries({ queryKey: ['conversations', 'detail', id] });
    void queryClient.invalidateQueries({ queryKey: ['conversations'] });
  };

  // Story 7.10b — follow-up execution history for this conversation.
  // Fetched in parallel with the main detail via TanStack; invalidated after
  // a manual retry so the card list refreshes immediately.
  const followupsQuery = useQuery({
    queryKey: ['conversations', 'followups', id],
    queryFn: () =>
      apiClient.get<{ items: FollowupExecutionListItem[] }>(`/admin/conversations/${id}/followups`),
    enabled: id.length > 0,
  });

  const closeMutation = useMutation({
    mutationFn: (reason: string) =>
      apiClient.post<{ id: string; status: ConversationStatus; already?: boolean }>(
        `/admin/conversations/${id}/close`,
        { closedReason: reason },
      ),
    onSuccess: (res) => {
      if (res.already) {
        toast.info('Conversa já estava encerrada.');
      } else {
        toast.success('Conversa encerrada.');
      }
      setConfirmOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: (err) => {
      toast.error(humanizeError(err, 'Falha ao encerrar conversa.'));
    },
  });

  if (detailQuery.isLoading) {
    return (
      <div data-testid="conversation-detail-skeleton" className="space-y-4">
        <div className="h-8 w-56 animate-pulse rounded bg-muted" />
        <div className="h-32 animate-pulse rounded bg-muted" />
        <div className="h-64 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (detailQuery.error) {
    const notFound = detailQuery.error instanceof ApiError && detailQuery.error.status === 404;
    return (
      <Card data-testid="conversation-detail-error">
        <CardContent className="flex flex-col items-start gap-3 p-6">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle aria-hidden className="h-4 w-4" />
            <span className="text-sm font-medium">
              {notFound ? 'Conversa não encontrada.' : 'Falha ao carregar conversa.'}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            {humanizeError(detailQuery.error, 'Erro desconhecido.')}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate('/conversations')}
          >
            <ArrowLeft aria-hidden className="mr-1 h-3 w-3" />
            Voltar à lista
          </Button>
        </CardContent>
      </Card>
    );
  }

  const d = detailQuery.data!;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/conversations"
            className="mb-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline"
          >
            <ArrowLeft aria-hidden className="h-3 w-3" />
            Conversas
          </Link>
          <h1
            className="text-2xl font-semibold tracking-tight"
            data-testid="conversation-detail-title"
          >
            {d.customer.name ?? 'Sem nome'}{' '}
            <span className="font-mono text-base text-muted-foreground">
              {d.customer.maskedPhone}
            </span>
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <StatusBadge status={d.status} />
            {d.customer.optedOut ? (
              <span
                data-testid="opted-out-badge"
                className="rounded-full bg-red-500/20 px-2 py-0.5 text-[11px] font-medium text-red-700 dark:text-red-300"
              >
                OPT-OUT {d.customer.optedOutAt ? `em ${formatTs(d.customer.optedOutAt)}` : ''}
              </span>
            ) : null}
            <span>•</span>
            <span>
              Campanha: <span className="font-medium text-foreground">{d.campaign.name}</span>
            </span>
            <span>•</span>
            <span>Aberta em {formatTs(d.createdAt)}</span>
            {d.status === 'closed' ? (
              <>
                <span>•</span>
                <span>Encerrada em {formatTs(d.updatedAt)}</span>
              </>
            ) : null}
          </div>
        </div>

        {canClose && d.status !== 'closed' ? (
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap">
            <HandoffActions conversationId={d.id} status={d.status} onChange={invalidateDetail} />
            <Button
              type="button"
              variant="destructive"
              onClick={() => setConfirmOpen(true)}
              data-testid="close-conversation-button"
            >
              <Power aria-hidden className="mr-1 h-3 w-3" />
              Encerrar conversa
            </Button>
          </div>
        ) : null}
      </div>

      {d.status === 'human_handoff' ? <HandoffBanner metadata={d.metadata} /> : null}

      {d.scheduledCallbacks.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock aria-hidden className="h-4 w-4" />
              Callbacks agendados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm" data-testid="scheduled-callbacks">
              {d.scheduledCallbacks.map((cb) => (
                <li key={cb.id} className="flex items-center gap-2">
                  <span className="font-mono text-xs">{formatTs(cb.scheduledFor)}</span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] ${
                      cb.status === 'executed'
                        ? 'bg-green-500/20 text-green-700 dark:text-green-300'
                        : cb.status === 'cancelled'
                          ? 'bg-red-500/20 text-red-700 dark:text-red-300'
                          : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {cb.status}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      {followupsQuery.data && followupsQuery.data.items.length > 0 ? (
        <Card data-testid="followup-executions-section">
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-base">
                Follow-ups ({followupsQuery.data.items.length})
              </CardTitle>
              {canRetryFollowup && d.status === 'active' ? (
                <Button size="sm" variant="outline" onClick={() => setRetryOpen(true)}>
                  Reenviar follow-up
                </Button>
              ) : null}
            </div>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {followupsQuery.data.items.map((f) => (
                <FollowupExecutionCard key={f.id} execution={f} />
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : canRetryFollowup && d.status === 'active' ? (
        <Card>
          <CardContent className="flex items-center justify-between gap-2 p-4 text-sm">
            <span className="text-muted-foreground">
              Nenhum follow-up disparado nesta conversa.
            </span>
            <Button size="sm" variant="outline" onClick={() => setRetryOpen(true)}>
              Reenviar follow-up manualmente
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Timeline ({d.messageCount})</CardTitle>
        </CardHeader>
        <CardContent>
          <MessageTimeline messages={d.messages} />
        </CardContent>
      </Card>

      {d.status === 'human_handoff' && canClose ? (
        <ManualComposer conversationId={d.id} onSent={invalidateDetail} />
      ) : null}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Encerrar conversa</DialogTitle>
            <DialogDescription>
              Essa ação marca a conversa como closed e não pode ser revertida pela UI.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label htmlFor="close-reason" className="text-sm font-medium">
              Motivo
            </label>
            <input
              id="close-reason"
              type="text"
              aria-label="Motivo de encerramento"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value)}
              maxLength={50}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              disabled={closeMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => closeMutation.mutate(closeReason.trim() || 'manual_operator')}
              disabled={closeMutation.isPending}
              data-testid="confirm-close-button"
            >
              Confirmar encerramento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RetryFollowupModal
        open={retryOpen}
        onOpenChange={setRetryOpen}
        conversationId={id}
        invalidateKeys={[
          ['conversations', 'followups', id],
          ['followups', 'list'],
          ['followups', 'aggregate', '24h'],
        ]}
      />
    </div>
  );
}

export default ConversationDetailPage;
